import { computed, watch } from 'vue'
import { useWeather } from './useWeather.js'
import { planFor } from '../common/js/weatherOverlays.js'

/**
 * 把一组渐变色标转成 CSS 渐变。自上而下，第一个颜色在顶部、最后一个在底部
 * （就是需求里「渐变顺序为第一个颜色到最后一个颜色」）。
 */
function toGradient(stops) {
  if (stops.length <= 1) return stops[0] || 'transparent'
  const parts = stops.map(
    (c, i) => `${c} ${Math.round((i / (stops.length - 1)) * 100)}%`
  )
  return `linear-gradient(180deg, ${parts.join(', ')})`
}

/**
 * 全站天气主题的唯一数据源。
 *
 *  - `theme`（响应式）—— 由 `weather.current.wx`（QWeather condition code）
 *    推出当前主题：背景渐变、文字明暗、前景元素层。
 *  - `applyTheme()` —— 把主题推到 `document.documentElement`：
 *      --bg-gradient / --bg-page / --bg-veil  → 页面背景
 *      data-page="dark|light"                 → 文字与交互态整体翻转
 *      data-theme="sunny|rain|snow|…"         → 便于 CSS 挂钩调试
 *
 * 模块 / 组件的 10% 白玻璃底是全局固定 token（见 style.css），不随天气变化。
 */
export function useTheme() {
  const { weather } = useWeather()

  const theme = computed(() => {
    const cur = weather.value?.current
    const code = cur?.wx != null ? cur.wx : ''
    const plan = planFor(code === '' ? '100' : code)
    const darkPage = plan.textMode === 'light' // 深色底 → 浅色文字
    return {
      key: plan.key,
      label: plan.label,
      overlay: plan.layers,
      gradient: plan.gradient,
      gradientCss: toGradient(plan.gradient),
      veil: plan.veil,
      darkPage
    }
  })

  function applyTheme(t = theme.value) {
    if (!t) return
    const root = document.documentElement
    root.style.setProperty('--bg-gradient', t.gradientCss)
    // 兜底纯色：body、iOS 过滚动区、以及不支持渐变的场景
    root.style.setProperty('--bg-page', t.gradient[0])
    root.style.setProperty('--bg-veil', t.veil)
    // hero 底部收边色 + 文字阴影方向，都跟着页面明暗走
    root.style.setProperty('--hero-bottom-fade', t.darkPage ? 'rgba(0,0,0,.18)' : 'rgba(255,255,255,.22)')
    root.style.setProperty('--hero-text-shadow', t.darkPage
      ? '0 1px 3px rgba(0,0,0,.30)'
      : '0 1px 2px rgba(255,255,255,.38)')
    root.dataset.page = t.darkPage ? 'dark' : 'light'
    root.dataset.theme = t.key
  }

  watch(theme, applyTheme, { immediate: true })

  return { theme, applyTheme }
}
